/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import { Drawer } from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "../../../../Common/Svgs/CloseIcon";
import { updateUsers } from "../../../../Redux/Slices/users.slice";
import capitalize from "../../../../Utils/capitalize";
import { formatPhoneNumber } from "../../../../Utils/phoneNumberHelper";
import { client } from "../../../../Utils/axiosClient";
import StarIcon from "../../../../Common/Svgs/StarIcon";
import CoinIcon from "../../../../Common/Svgs/CoinIcon";
import EnergyCircleIcon from "../../../../Common/Svgs/EnergyCircleIcon";
import RentalCircleIcon from "../../../../Common/Svgs/RentalCircleIcon";

const Tag = ({ text = "" }) => {
  return (
    <div className="bg-primary-10 py-1 px-2 max-h-[26px] rounded-md max-w-max">
      <p className="font-poppins font-medium text-xs text-primary">{text}</p>
    </div>
  );
};

const LabelAndValue = ({ label = "", value = "", shouldCapitalize = true }) => {
  return (
    <div className="flex-1 font-poppins">
      <p
        className="font-medium text-sm"
        style={{ textTransform: shouldCapitalize && "capitalize" }}
      >
        {value || "--"}
      </p>

      <p className="mt-1 font-normal text-xs text-dark-gray">{label}</p>
    </div>
  );
};

const DriverDetailDrawer = ({ visible, onCancel }) => {
  const dispatch = useDispatch();

  const { driverDetailDrawer } = useSelector((state) => state.users);

  const data = driverDetailDrawer?.data || {};

  const [driverRating, setDriverRating] = useState([]);
  const [timeoffData, setTimeoffData] = useState({
    used: 0,
    total: 10,
    pending: 0,
  });

  useEffect(() => {
    try {
      const fetchData = async () => {
        const res = await client.get(
          `${process.env.REACT_APP_API_URL}/drivers/get-rating`,
          {
            params: {
              driverId: data?.id,
            },
          },
        );
        setDriverRating(res?.data?.avgRating);
      };
      if (data?.id) {
        fetchData();
      }
    } catch (error) {
      console.log("error while fetching avg rating ", error);
    }
  }, [data?.id]);

  useEffect(() => {
    const fetchTimeoff = async () => {
      try {
        const res = await client.get(
          `${process.env.REACT_APP_API_URL}/drivers/time-off-summary`,
          {
            params: { driverId: data?.id },
          },
        );

        setTimeoffData(res?.data);
      } catch (err) {
        console.log("timeoff error", err);
      }
    };

    if (data?.id) fetchTimeoff();
  }, [data?.id]);

  const handleViewLicense = () => {
    if (data?.licenseUrl) {
      dispatch(
        updateUsers({
          key: "licenseModal",
          value: {
            visible: true,
            url: data?.licenseUrl || "",
            type: "driver",
          },
        }),
      );

      return;
    }

    dispatch(
      updateUsers({
        key: "showToastMessage",
        value: {
          visible: true,
          data: {
            title: "No License Found",
            description: `License not found for driver ${capitalize(
              data?.firstName,
            )} ${capitalize(data?.lastName)}`,
            type: "error",
          },
        },
      }),
    );
  };

  const handleViewFeedback = () => {
    dispatch(
      updateUsers({
        key: "driverFeedbackDrawer",
        value: {
          visible: true,
          driverId: data?.id,
          avgRating: driverRating,
        },
      }),
    );
  };

  const handleViewRequests = () => {
    dispatch(
      updateUsers({
        key: "driverRequestDrawer",
        value: {
          visible: true,
          data: {},
        },
      }),
    );
  };

  return (
    <Drawer
      width={560}
      placement="right"
      closable={false}
      maskStyle={{
        background: "transparent",
        boxShadow: "12px 12px 16px -4px #10182814",
      }}
      open={visible}
      onClose={onCancel}
      className="rounded-tl-xl"
    >
      <div className="p-6 w-full h-max bg-background-white rounded-tl-xl relative">
        <div className="flex items-center justify-between">
          <p className="font-poppins font-medium text-base text-black">
            Driver Details
          </p>

          <CloseIcon
            className="w-5 h-5 cursor-pointer stroke-dark-grey-text"
            onClick={onCancel}
          />
        </div>

        <div className="mt-6 px-6 py-4 border border-light-grey rounded-xl">
          <Tag text="General" />

          <div className="mt-4 flex items-center gap-x-4">
            <LabelAndValue label="First Name" value={data?.firstName} />
            <LabelAndValue label="Last Name" value={data?.lastName} />
          </div>

          <div className="mt-4 flex items-center gap-x-4">
            <LabelAndValue label="Gender" value={data?.gender} />

            <LabelAndValue
              label="Contact Number"
              value={formatPhoneNumber(data?.contactNo)}
            />
          </div>

          <div className="mt-4 flex items-center gap-x-4">
            <LabelAndValue
              label="M-Pesa Number"
              value={formatPhoneNumber(data?.mpesaNo)}
            />

            <LabelAndValue label="Hub Station" value={data?.hubName} />
          </div>

          <div className="mt-4 flex items-center gap-x-4">
            <LabelAndValue
              label={data?.appUserId ? "User ID" : "Email ID"}
              value={data?.appUserId || data?.email}
              shouldCapitalize={false}
            />

            <div className="flex-1">
              <div className="flex items-center gap-x-1">
                <div className="bg-primary-10 rounded-md flex items-center gap-x-1 pl-1 py-1 pr-2">
                  <CoinIcon size={12} />
                  <p className="font-poppins font-semibold text-10 leading-[100%] text-primary whitespace-nowrap">
                    {data?.commission} KSH
                  </p>
                </div>

                <div className="bg-primary-10 rounded-md flex items-center gap-x-1 pl-1 py-1 pr-2">
                  <EnergyCircleIcon />
                  <p className="font-poppins font-semibold text-10 leading-[100%] text-primary whitespace-nowrap">
                    {data?.energy} kWh
                  </p>
                </div>

                <div className="bg-primary-10 rounded-md flex items-center gap-x-1 pl-1 py-1 pr-2">
                  <RentalCircleIcon />
                  <p className="font-poppins font-semibold text-10 leading-[100%] text-primary whitespace-nowrap">
                    {data?.rentals} Rentals
                  </p>
                </div>
              </div>

              <p className="mt-1 font-poppins font-normal text-xs text-dark-gray">
                Wallet Balance
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 px-6 py-4 border border-light-grey rounded-xl">
          <div className="flex items-start justify-between">
            <Tag text="License" />

            <button
              type="button"
              className="py-1 h-[26px] px-4 bg-primary hover:bg-primary-50 rounded-md"
              onClick={handleViewLicense}
            >
              <p className="font-poppins font-medium text-xs text-white">
                View License
              </p>
            </button>
          </div>

          <div className="mt-4 flex items-center gap-x-4">
            <LabelAndValue
              label="License Number"
              value={data?.licenseId}
              shouldCapitalize={false}
            />

            <LabelAndValue
              label="Validity"
              value={
                data?.licenseValidity
                  ? dayjs(data?.licenseValidity).format("DD-MM-YYYY")
                  : "--"
              }
            />
          </div>
        </div>

        <div className="mt-6 px-6 py-4 border border-light-grey rounded-xl">
          <div className="flex items-start justify-between">
            <Tag text="Performance Rating" />

            <button
              type="button"
              className="py-1 h-[26px] px-4 bg-primary hover:bg-primary-50 rounded-md"
              onClick={handleViewFeedback}
            >
              <p className="font-poppins font-medium text-xs text-white">
                View Feedback
              </p>
            </button>
          </div>

          <div className="mt-4 flex items-center gap-x-4">
            <LabelAndValue
              label="Average"
              value={
                <div className="flex gap-x-1">
                  <StarIcon size={18} fill="#FFD700" stroke="none" />

                  <p className="font-poppins font-medium text-sm">
                    {driverRating || 0}
                  </p>
                </div>
              }
              shouldCapitalize={false}
            />

            <LabelAndValue label="Trips" value={data?.trips} />
          </div>
        </div>

        <div className="mt-6 px-6 py-4 border border-light-grey rounded-xl">
          <div className="flex items-start justify-between">
            <Tag text="Time-off" />

            <button
              type="button"
              className="py-1 h-[26px] px-4 bg-primary hover:bg-primary-50 rounded-md"
              onClick={handleViewRequests}
            >
              <p className="font-poppins font-medium text-xs leading-[100%] text-white">
                View Requests
              </p>
            </button>
          </div>

          <div className="mt-4 flex items-center gap-x-4">
            <div className="flex-1 font-poppins">
              <p className="font-medium text-sm">
                {timeoffData.total - timeoffData.used}/
                <span className="font-light text-xs">{timeoffData.total}</span>
              </p>
              <p className="font-normal text-xs text-dark-gray">
                Available This Month
              </p>
            </div>
            <div className="flex-1 font-poppins">
              <p className="font-medium text-sm">{timeoffData.pending}</p>

              <p className="mt-1 font-normal text-xs text-dark-gray">
                Pending Requests
              </p>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default DriverDetailDrawer;
